<template>
  <v-container class="fill-height" fluid>
    <v-row align="center" justify="center">
      <v-col cols="12" sm="8" md="4" style="max-width: 400px">
        <v-card
          shaped
          class="elevation-24"
          :style="{ 'border-bottom-right-radius': '0px' }"
        >
          <v-list-item class="pt-6">
            <v-list-item-content>
              <v-img src="@/assets/logo-bni-qris.webp" max-width="8rem"></v-img>

              <h2 class="mt-4 font-weight-black">Change Password</h2>
            </v-list-item-content>
          </v-list-item>

          <v-card-text style="padding-bottom: 4px">
            <div class="grey--text text-justify">
              <!-- SonarQube Duplicate Suppression: Reason: Duplicated code is necessary due to conditional logic -->
              <p v-if="forcelast == true">
                You need to update your password because your password has
                expired for a maximum of 60 days.
              </p>
              <p v-else>
                You need to update your password because this is the first time
                you are signing in.
              </p>
            </div>
            <v-form ref="form" lazy-validation v-on:submit.prevent="">
              <!-- SonarQube Duplicate Suppression: Reason: Different forms -->
              <v-text-field
                id="passwordCurrent"
                label="Current Password"
                name="passwordCurrent"
                prepend-icon="mdi-lock-outline"
                v-model="passwordCurrent"
                v-on:keyup.enter="changePassword"
                :error-messages="passwordCurrentErrors"
                @change="$v.passwordCurrent.$touch()"
                @blur="$v.passwordCurrent.$touch()"
                @input="$v.passwordCurrent.$touch()"
                :append-icon="
                  passwordCurrentFieldType ? 'mdi-eye-off' : 'mdi-eye'
                "
                @click:append="
                  () => (passwordCurrentFieldType = !passwordCurrentFieldType)
                "
                :type="passwordCurrentFieldType ? 'password' : 'text'"
              />

              <!-- Other fields for the first form -->

              <!-- SonarQube Duplicate Suppression: Reason: Different forms -->
              <v-text-field
                id="password"
                label="New Password"
                name="password"
                prepend-icon="mdi-lock"
                v-model="password"
                v-on:keyup.enter="changePassword"
                :error-messages="passwordErrors"
                @change="$v.password.$touch()"
                @blur="$v.password.$touch()"
                @input="$v.password.$touch()"
                :append-icon="passwordFieldType ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append="() => (passwordFieldType = !passwordFieldType)"
                :type="passwordFieldType ? 'password' : 'text'"
              />

              <!-- Other fields for the second form -->
              <v-text-field
                id="passwordConfirmation"
                label="Confirm New Password"
                name="passwordConfirmation"
                prepend-icon="mdi-lock-check"
                v-model="passwordConfirmation"
                v-on:keyup.enter="changePassword"
                :error-messages="passwordConfirmationErrors"
                @change="$v.passwordConfirmation.$touch()"
                @blur="$v.passwordConfirmation.$touch()"
                @input="$v.passwordConfirmation.$touch()"
                :append-icon="
                  passwordConfirmFieldType ? 'mdi-eye-off' : 'mdi-eye'
                "
                @click:append="
                  () => (passwordConfirmFieldType = !passwordConfirmFieldType)
                "
                :type="passwordConfirmFieldType ? 'password' : 'text'"
              />
            </v-form>
          </v-card-text>
          <v-card-actions
            style="
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 8px 16px 16px 16px;
            "
          >
            <v-btn
              color="orange"
              @click="back"
              :disabled="loading"
              style="color: white"
              >back</v-btn
            >
            <v-btn color="#005f7a" style="color: white" @click="changePassword"
              >Save</v-btn
            >
          </v-card-actions>
          <v-progress-linear
            :active="loading"
            :indeterminate="loading"
            absolute
            bottom
            color="red lighten-2"
          />
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { sameAs } from 'vuelidate/lib/validators';
import decryptUserData from '@/lib/utils/decryptUserData';
import CustomEncryption from '../lib/CustomEncryption';
import { dataExtraString3 } from '../store/params';

export default {
  name: 'ChangePassword',
  props: {
    source: {
      type: String,
      default: null,
    },
    forcelast: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [validationMixin],
  validations: {
    password: {
      required(value) {
        return value != '';
      },
      minLength(value) {
        return value.length >= 12;
      },
      upperCase(value) {
        const pattern = /[A-Z]/;
        return !!pattern.test(value);
      },
      lowerCase(value) {
        const pattern = /[a-z]/;
        return !!pattern.test(value);
      },
      specialChar(value) {
        const pattern = /[!-/:@[-`{-~]/;
        return !!pattern.test(value);
      },
      numeric(value) {
        const pattern = /\d/;
        return !!pattern.test(value);
      },
    },
    passwordConfirmation: {
      required(value) {
        return value != '';
      },
      sameAsPassword: sameAs('password'),
    },
    passwordCurrent: {
      required(value) {
        return value != '';
      },
    },
  },
  data() {
    return {
      passwordFieldType: true,
      passwordConfirmFieldType: true,
      passwordCurrentFieldType: true,
      password: '',
      passwordConfirmation: '',
      passwordCurrent: '',
      passChanged: false,
    };
  },
  mounted() {
    const userData = JSON.parse(localStorage.getItem('logged_in_qram'))?.user;
    const decryptedUserData = decryptUserData(userData);
    
    if (!decryptedUserData || !decryptedUserData.force_password_change) {
      this.$router.push({ name: 'Dashboard' });
    }
  },
  computed: {
    loading() {
      const loading =
        this.$store.state.loading || this.$store.state.logoutLoading;
      return loading;
    },

    passwordErrors() {
      const errors = [];
      if (!this.$v.password.$dirty) return errors;

      if (!this.$v.password.required) errors.push('New Password is required');
      if (!this.$v.password.lowerCase) {
        errors.push('New Password must have lowercase character');
      }
      if (!this.$v.password.upperCase) {
        errors.push('New Password must have uppercase character');
      }
      if (!this.$v.password.numeric) {
        errors.push('New Password must have numeric character');
      }
      if (!this.$v.password.specialChar) {
        errors.push('New Password must have special character');
      }
      if (!this.$v.password.minLength) {
        errors.push('New Password must be at least 12 characters long');
      }
      return errors;
    },
    passwordConfirmationErrors() {
      const errors = [];
      if (!this.$v.passwordConfirmation.$dirty) return errors;
      if (!this.$v.passwordConfirmation.required) {
        errors.push('Password Confirmation is required');
      }
      if (!this.$v.passwordConfirmation.sameAsPassword) {
        errors.push('Passwords must be identical.');
      }
      return errors;
    },
    passwordCurrentErrors() {
      const errors = [];
      if (!this.$v.passwordCurrent.$dirty) return errors;
      if (!this.$v.passwordCurrent.required) {
        errors.push('Current Password is required');
      }
      return errors;
    },
  },

  methods: {
    async changePassword() {
      this.$v.$touch();
      if (!this.$v.$invalid && !this.loading) {
        // eslint-disable-next-line new-cap
        const speEncrypt = new CustomEncryption();
        const extras = speEncrypt.decryption({
          enc: process.env.VUE_APP_STRING_EXTRA2,
          masterkey: dataExtraString3,
        });
        this.$store
          .dispatch('changeForcePassword', {
            password: this.password,
            passwordConfirmation: this.passwordConfirmation,
            passwordCurrent: this.passwordCurrent,
            encryptedPassword: speEncrypt.encrypt(
              speEncrypt.encryption(this.password),
              '',
              extras
            ),
            encryptedPasswordConfirmation: speEncrypt.encrypt(
              speEncrypt.encryption(this.passwordConfirmation),
              '',
              extras
            ),
            encryptedPasswordCurrent: speEncrypt.encrypt(
              speEncrypt.encryption(this.passwordCurrent),
              '',
              extras
            ),
          })
          .then((res) => {
            if (res) {
              this.$store.dispatch('logout');
            }
          });
      }
    },

    back() {
      this.$store.dispatch('logout');
    },
  },
};
</script>

<style scoped>
.border-top {
  border-radius: 24px 0px 0px 0px;
}
.header-logo {
  align-self: flex-start;
  position: absolute;
  left: 0;
  top: 0;
}
</style>
